import { Injectable } from '@angular/core';
import { Capacitor  } from '@capacitor/core';
import { Storage } from '@ionic/storage-angular';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
//import { FirebaseAnalytics } from "@capacitor-community/firebase-analytics";

// Datos del usuario que Meta usa para atribuir las conversiones (Advanced Matching en el píxel y CAPI en el GTM de servidor)
export interface DataLayerUserData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  postalCode?: string;
  externalId?: string | number;
}

@Injectable({
  providedIn: 'root',
})
export class DataLayerService {

  constructor(
    private storage: Storage
    ) {}

  public pushEcomEventGTM(nameEvent: string, params: any){
    const config = {
      event: nameEvent,
      event_id: this.generateEventId(),
      ecommerce: params
    };

    (<any>window).dataLayer = (<any>window).dataLayer || [];
    (<any>window).dataLayer.push(config);
  }

  public pushCustomEventGTM(nameEvent: string){
    (<any>window).dataLayer = (<any>window).dataLayer || [];
    (<any>window).dataLayer.push({
      event: nameEvent,
      event_id: this.generateEventId()
    });
  }

  // En las apps la URL es https://localhost (Android) o capacitor://localhost (iOS); GTM las distingue por app_platform
  public pushPageView(){
    const pageLocation = window.location.href;
    const pagePath = window.location.pathname;
    const pageTitle = document.title;

    (<any>window).dataLayer = (<any>window).dataLayer || [];
    (<any>window).dataLayer.push({
      event: 'page_view',
      event_id: this.generateEventId(),
      page_location: pageLocation,
      page_path: pagePath,
      page_title: pageTitle
    });
  }

  // Captura el fbclid antes de que el router limpie la URL y lo guarda en la cookie _fbc para GTM
  public captureFbclid(){
    if (Capacitor.getPlatform() === 'web') {
      const fbclid = new URLSearchParams(window.location.search).get('fbclid');
      if (fbclid) {
        const fbc = 'fb.1.' + new Date().getTime() + '.' + fbclid;
        document.cookie = '_fbc=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie = '_fbc=' + fbc + '; path=/; max-age=' + (60 * 60 * 24 * 90) + '; SameSite=Lax';

        (<any>window).dataLayer = (<any>window).dataLayer || [];
        (<any>window).dataLayer.push({ fbclid: fbclid });
      }
    }
  }

  // Deja en el dataLayer los datos del usuario normalizados y hasheados en SHA-256 (formato que pide Meta) para que las
  // etiquetas de GTM los adjunten a los eventos. No hay que esperar a que termine salvo que justo después se mande un evento
  public async setUserData(data: DataLayerUserData): Promise<void> {
    if (!data?.email || !window.crypto?.subtle) {
      return;
    }
    // Si un admin está suplantando a un usuario no queremos atribuir su navegador a ese usuario
    if (await this.storage.get('IMPERSONATING')) {
      return;
    }
    try {
      const userData = {
        em: await this.sha256(this.normalizeText(data.email)),
        ph: await this.sha256(this.normalizePhone(data.phone)),
        fn: await this.sha256(this.normalizeText(data.firstName)),
        ln: await this.sha256(this.normalizeText(data.lastName)),
        zp: await this.sha256(this.normalizePostalCode(data.postalCode)),
        external_id: await this.sha256(data.externalId != null ? String(data.externalId).trim() : null),
      };
      Object.keys(userData).forEach(key => userData[key] === undefined && delete userData[key]);

      (<any>window).dataLayer = (<any>window).dataLayer || [];
      (<any>window).dataLayer.push({ user_data: userData });
    } catch (e) {
      console.error('[DataLayer] Error al preparar user_data', e);
    }
  }

  // A partir del perfil que devuelve el back (o el guardado en storage al arrancar)
  public setUserDataFromProfile(profile: any): Promise<void> {
    return this.setUserData({
      email: profile?.username,
      phone: profile?.mobile,
      firstName: profile?.name,
      lastName: profile?.surname,
      postalCode: profile?.taxPostalCode,
      externalId: profile?.internalId,
    });
  }

  public async restoreUserData(): Promise<void> {
    const profile = await this.storage.get('PROFILE');
    if (profile) {
      return this.setUserDataFromProfile(profile);
    }
  }

  public clearUserData(){
    (<any>window).dataLayer = (<any>window).dataLayer || [];
    (<any>window).dataLayer.push({ user_data: undefined });
  }

  // En las apps el consentimiento no lo da Cookiebot sino la app: en iOS sigue a ATT (igual que PostHog) y en Android se
  // concede. Luego lanza cookie_consent_update, el evento con el que GTM carga HubSpot. Hay que llamarlo al arrancar y
  // cada vez que el usuario responde a ATT
  public async updateNativeConsent(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    let granted = true;
    if (Capacitor.getPlatform() === 'ios') {
      try {
        granted = (await AppTrackingTransparency.getStatus()).status === 'authorized';
      } catch (e) {
        granted = false;
      }
    }
    const state = granted ? 'granted' : 'denied';
    this.gtag('consent', 'update', {
      ad_storage: state,
      ad_user_data: state,
      ad_personalization: state,
      analytics_storage: state
    });
    (<any>window).dataLayer.push({ event: 'cookie_consent_update' });
  }

  // GTM solo reconoce los comandos de gtag si llegan como el objeto arguments, no como un array
  private gtag(..._args: any[]) {
    (<any>window).dataLayer = (<any>window).dataLayer || [];
    (<any>window).dataLayer.push(arguments);
  }

  // Mismo id en el píxel y en CAPI para que Meta pueda deduplicar el evento
  private generateEventId(): string {
    const crypto: any = window.crypto;
    if (crypto?.randomUUID) {
      return crypto.randomUUID();
    }
    return Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 12);
  }

  private normalizeText(value: any): string {
    return value ? String(value).trim().toLowerCase() : null;
  }

  // Meta pide solo dígitos y con prefijo de país. Los móviles españoles se guardan a veces sin prefijo
  private normalizePhone(value: any): string {
    if (!value) {
      return null;
    }
    let phone = String(value).replace(/\D/g, '').replace(/^00/, '');
    if (phone.length === 9 && /^[6789]/.test(phone)) {
      phone = '34' + phone;
    }
    return phone || null;
  }

  private normalizePostalCode(value: any): string {
    return value ? String(value).replace(/\s/g, '').toLowerCase() : null;
  }

  private async sha256(value: string): Promise<string> {
    if (!value) {
      return undefined;
    }
    const buffer = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(buffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  }

}
